<script lang="ts" setup>
import type { HeaderSettings, LoginMenuSettings } from '@convatec/180medical-website/types/app'
import type { DropdownMenuItem } from '@nuxt/ui'

interface Props {
  headerSettings: HeaderSettings
  loginMenuSettings: LoginMenuSettings
}

const props = withDefaults(defineProps<Props>(), {})
const route = useRoute()
const searchTerm = ref('')
const appConfig = useAppConfig()
const uInputRef = ref(null)
const links: DropdownMenuItem[] = []
props.loginMenuSettings.links?.forEach((l) => {
  links.push({
    label: l.text,
    icon: appConfig.ui.icons[l.iconName || ''],
    target: l.target,
    to: l.url,
  })
})

const items = ref<DropdownMenuItem[]>(links)

function search() {
  if (!searchTerm?.value?.trim()) {
    return
  }

  if (route.query.searchTerm !== searchTerm.value.trim()) {
    navigateTo({
      path: props.headerSettings?.searchUrl,
      query: {
        ...route.query,
        page: 1,
        searchTerm: searchTerm.value.trim(),
      },
    })
  }
}

function clearSearch() {
  searchTerm.value = ''
  nextTick(() => {
    (uInputRef?.value as any)?.inputRef?.focus()
  })
}
</script>

<template>
  <div
    class="med-upper-header hidden xl:grid relative px-7.5 py-2 laptop:grid items-center bg-med-deep-navy dark-bg md:px-15 xl:px-20 gap-4"
    style="grid-template-columns: 1fr 1fr"
  >
    <div class="flex pl-52 2xl:pl-64">
      <div class="xl:w-75 2xl:w-107.5 max-w-full">
        <div class="inline-form flex">
          <UFormField class="gap-0 w-full">
            <UInput
              id="headerSearchInput"
              ref="uInputRef"
              v-model="searchTerm"
              aria-label="Search"
              :placeholder="props.headerSettings?.searchPlaceholderText || ''"
              size="lg"
              class="h-10 w-full rounded-xl border-0 bg-white shadow-0"
              variant="none"
              :ui="{
                base: 'shadow-none',
              }"
              @keyup.enter="search"
            >
              <template
                #trailing
              >
                <UButton
                  v-if="searchTerm?.length > 0"
                  class="cursor-pointer"
                  variant="ghost"
                  size="lg"
                  :icon="appConfig.ui.icons.close"
                  aria-label="Clear search"
                  @click="clearSearch()"
                />
                <UButton
                  class="cursor-pointer"
                  variant="ghost"
                  size="lg"
                  :icon="appConfig.ui.icons.search"
                  aria-label="Search"
                  @click="search()"
                />
              </template>
            </UInput>
          </UFormField>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-end gap-3.5 shrink-0">
      <div class="flex flex-row gap-4">
        <div class="text-sm text-white font-bold flex items-center gap-1">
          <span class="shrink-0">
            <img
              src="/images/icon-phone-sm.svg"
              alt="phone icon"
              class="inline-block"
            >
            {{ props.headerSettings?.contactNumberLabel || '' }}:
          </span>
          <div
            class="contact-tooltip"
            v-html="`<a href='tel:${props.headerSettings?.contactNumber}'>${props.headerSettings?.contactNumber}</a>`"
          />
        </div>
        <UButton
          color="info"
          variant="solid"
          size="lg"
          class="min-w-[150px] justify-center h-11 text-primary"
          :to="useCmsUrl(props.headerSettings?.linkButton?.url || '')"
          :locale="false"
        >
          {{ props.headerSettings?.linkButton?.text || '' }}
        </UButton>
      </div>
      <UDropdownMenu
        v-slot="{ open }"
        :items="items"
        :content="{
          align: 'end',
        }"
        :ui="{
          content: 'w-[345px] py-4 text-sm rounded-xl bg-white card-shadow',
          item: 'text-sm text-primary font-semibold underline',
          itemLeadingIcon: 'text-primary size-6',
          itemLabelExternalIcon: 'hidden',
        }"
        size="xl"
      >
        <UButton
          size="lg"
          :label="props.loginMenuSettings.buttonText"
          color="primary"
          variant="solid"
          class="min-w-[150px] h-[40px] justify-center fill-current ring-1 text-white ring-white"
          :trailing-icon="open
            ? appConfig.ui.icons.closeCircle
            : appConfig.ui.icons.chevronDown"
        />
      </UDropdownMenu>
    </div>
  </div>
</template>

<style lang="scss">
.contact-tooltip {
  a {
    color: var(--ui-info);
    text-decoration: underline;
  }
}
</style>
